import type { Middleware, MutationHook } from "react-query-kit";

/**
 * Creates a middleware that adds a hard page reload after a successful mutation.
 *
 * This middleware enhances a mutation hook by adding functionality to reload the page
 * (via `window.location.reload()`) after the mutation has successfully completed,
 * while still preserving the original `onSuccess` callback.
 *
 * @template TData - The data type returned by the mutation
 * @template TVariables - The variables type accepted by the mutation
 * @template TError - The error type that can be returned by the mutation
 * @template TContext - The context type used by the mutation
 *
 * @returns A middleware function that can be used with React Query/TanStack Query mutations
 *
 * @example
 * // Using with createMutation from React Query Kit
 * const useCreateTodo = createMutation({
 *   mutationFn: (todo) => api.createTodo(todo),
 *   middlewares: [hardReloadAfterMutation()]
 * });
 */
export function hardReloadAfterMutation<
  TData,
  TVariables,
  TError,
  TContext,
>(): Middleware<MutationHook<TData, TVariables, TError, TContext>> {
  return (useMutationNext) => {
    return ({ onSuccess, ...options }) => {
      return useMutationNext({
        ...options,
        onSuccess: (data, variables, context) => {
          onSuccess?.(data, variables, context);
          // Force a hard reload after mutation success
          window.location.reload();
        },
      });
    };
  };
}
